import jsPDF from "jspdf";
import type { GeneratedStory } from "@/lib/mock-story";
import robotoRegularUrl from "@/lib/fonts/Roboto-Regular.ttf";
import robotoBoldUrl from "@/lib/fonts/Roboto-Bold.ttf";

let fontsLoaded = false;
let robotoRegularBase64 = "";
let robotoBoldBase64 = "";

async function loadFonts() {
  if (fontsLoaded) return;

  const [regularBuf, boldBuf] = await Promise.all([
    fetch(robotoRegularUrl).then((r) => r.arrayBuffer()),
    fetch(robotoBoldUrl).then((r) => r.arrayBuffer()),
  ]);

  const toBase64 = (buf: ArrayBuffer) => {
    const bytes = new Uint8Array(buf);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  robotoRegularBase64 = toBase64(regularBuf);
  robotoBoldBase64 = toBase64(boldBuf);
  fontsLoaded = true;
}

function registerFonts(doc: jsPDF) {
  doc.addFileToVFS("Roboto-Regular.ttf", robotoRegularBase64);
  doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
  doc.addFileToVFS("Roboto-Bold.ttf", robotoBoldBase64);
  doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");
}

export async function downloadStoryPdf(storyData: GeneratedStory, childName: string) {
  await loadFonts();

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  registerFonts(doc);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 25;
  const maxWidth = pageWidth - margin * 2;
  let y = 40;

  const addPageIfNeeded = (needed: number) => {
    if (y + needed > pageHeight - 25) {
      doc.addPage();
      y = 30;
    }
  };

  // Title
  doc.setFont("Roboto", "bold");
  doc.setFontSize(22);
  const titleLines = doc.splitTextToSize(`Сказка для ${childName}`, maxWidth);
  doc.text(titleLines, pageWidth / 2, y, { align: "center" });
  y += titleLines.length * 10 + 12;

  // Divider
  doc.setDrawColor(200, 195, 185);
  doc.line(pageWidth / 2 - 20, y, pageWidth / 2 + 20, y);
  y += 15;

  // Story body
  doc.setFont("Roboto", "normal");
  doc.setFontSize(13);
  const paragraphs = storyData.story.split(/\n\n+/);

  for (const paragraph of paragraphs) {
    const lines = doc.splitTextToSize(paragraph.trim(), maxWidth);
    const blockHeight = lines.length * 6.5;
    addPageIfNeeded(blockHeight);
    doc.text(lines, margin, y);
    y += blockHeight + 5;
  }

  // Parent tip section
  if (storyData.parentTip) {
    y += 10;
    addPageIfNeeded(30);
    doc.setDrawColor(200, 195, 185);
    doc.line(margin, y, pageWidth - margin, y);
    y += 10;

    doc.setFont("Roboto", "bold");
    doc.setFontSize(14);
    doc.text("Для родителей", margin, y);
    y += 9;

    doc.setFont("Roboto", "normal");
    doc.setFontSize(11);
    const tipLines = doc.splitTextToSize(storyData.parentTip, maxWidth);
    addPageIfNeeded(tipLines.length * 5.5);
    doc.text(tipLines, margin, y);
    y += tipLines.length * 5.5 + 8;
  }

  // Discussion questions
  if (storyData.discussionQuestions?.length) {
    addPageIfNeeded(20);
    doc.setFont("Roboto", "bold");
    doc.setFontSize(12);
    doc.text("Вопросы для обсуждения:", margin, y);
    y += 8;

    doc.setFont("Roboto", "normal");
    doc.setFontSize(11);
    for (const q of storyData.discussionQuestions) {
      const qLines = doc.splitTextToSize(`• ${q}`, maxWidth - 5);
      addPageIfNeeded(qLines.length * 5.5);
      doc.text(qLines, margin + 3, y);
      y += qLines.length * 5.5 + 3;
    }
  }

  doc.save(`skazka-${childName.toLowerCase()}.pdf`);
}
